import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, storage } from '../../firebase';
import { toast } from 'react-hot-toast';

const OnboardingStepProfile = ({
  profile,
  onChange,
  onNext,
  onSkip,
  onBack
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const storageRef = ref(storage, `profilePictures/${auth.currentUser.uid}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      onChange('profilePicture', url);
      toast.success('Photo uploaded');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="mb-10">
        <h2 className="text-[1.75rem] sm:text-[2rem] font-light tracking-tight text-gray-900 mb-2">
          Build your profile
        </h2>
        <p className="text-[15px] text-gray-400">
          Help clients get to know you. You can always update this later.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photo upload — simple and clean */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => fileInputRef.current.click()}
            disabled={uploading}
            className="relative w-[72px] h-[72px] rounded-full bg-gray-100 border-2 border-dashed border-gray-200 hover:border-[#f97316]/40 flex items-center justify-center overflow-hidden transition-colors flex-shrink-0 group"
          >
            {profile.profilePicture ? (
              <img
                src={profile.profilePicture}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-[22px] text-gray-300 group-hover:text-[#f97316]/50 transition-colors">+</span>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-[#f97316] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
            disabled={uploading}
          />
          <div>
            <p className="text-[14px] text-gray-600">Profile photo</p>
            <p className="text-[13px] text-gray-400">Optional</p>
          </div>
        </div>

        <div>
          <label className="block text-[13px] text-gray-500 mb-2">
            What do you do?
          </label>
          <input
            type="text"
            value={profile.professionalTitle}
            onChange={(e) => onChange('professionalTitle', e.target.value)}
            className="w-full px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-gray-900 text-[15px] placeholder-gray-300 focus:outline-none focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316]/20 transition-all"
            placeholder="Wedding Photographer"
          />
        </div>

        <div>
          <label className="block text-[13px] text-gray-500 mb-2">
            Short bio
          </label>
          <textarea
            value={profile.bio}
            onChange={(e) => onChange('bio', e.target.value)}
            rows={3}
            className="w-full px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-gray-900 text-[15px] placeholder-gray-300 focus:outline-none focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316]/20 transition-all resize-none"
            placeholder="A few words about you and your work..."
          />
        </div>

        <div className="flex items-center justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="text-[14px] text-gray-400 hover:text-gray-600 transition-colors"
          >
            Back
          </button>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={onSkip}
              className="text-[14px] text-gray-400 hover:text-gray-600 transition-colors"
            >
              Skip
            </button>
            <button
              type="submit"
              className="px-8 py-3 bg-[#f97316] hover:bg-[#ea580c] text-white text-[14px] font-medium rounded-xl transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OnboardingStepProfile;
